// src/admin/legal/LegalVersionsTable.jsx — every version, TOS first then Privacy (normaliseList), newest first.
// Stato is display only (legalModel.statusOf). «Correggi» only where the BE says `correctable: true`.
// ⛔ No delete action on any row: a version is never deleted (BE contract).
// PDF Brian: a download (the route needs the token — the page fetches the Blob) and the short SHA-256, the whole
// value and the size in the tooltip, plus a copy button. No copy stored (sha256 null) → «—».
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Download } from 'lucide-react';
import AdminTable from '../components/AdminTable.jsx';
import { useClipboard } from '../../hooks/useClipboard.js';
import VersionLinks from './VersionLinks.jsx';
import {
  statusOf, canCorrect, shortHash, formatDateTime, formatSize, documentLabelKey,
} from './legalModel.js';
import { chipStyle, linkButtonStyle, monoStyle } from './legalFormStyles.js';
import { COLORS, RADII, SEMANTIC } from '../../constants/theme.js';

const STATUS_PALETTE = { inForce: SEMANTIC.verde, scheduled: SEMANTIC.info, superseded: SEMANTIC.neutro };
const ICON_BUTTON = { background: 'none', border: 'none', padding: '2px', cursor: 'pointer', color: COLORS.textSecondary };
const rowKey = (r) => `${r.document}:${r.version}`;

function HashCell({ row, onDownload, copiedKey, onCopy }) {
  const { t, i18n } = useTranslation('admin');
  if (typeof row.sha256 !== 'string') return <span>—</span>;
  const docName = documentLabelKey(row.document) ? t(documentLabelKey(row.document)) : row.document;
  const copied = copiedKey === rowKey(row);
  return (
    <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
      <button
        type="button" style={ICON_BUTTON} onClick={() => onDownload(row)}
        aria-label={t('legalDocuments.download', { document: docName, version: row.version })}
      >
        <Download size={15} />
      </button>
      <span
        style={monoStyle}
        title={t('legalDocuments.hashTooltip', { sha: row.sha256, size: formatSize(row.size_bytes, i18n.language) || '—' })}
      >
        {shortHash(row.sha256)}
      </span>
      <button
        type="button" style={ICON_BUTTON} onClick={() => onCopy(row)}
        aria-label={copied ? t('legalDocuments.hashCopied') : t('legalDocuments.copyHash')}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </span>
  );
}

export default function LegalVersionsTable({ list, loading, onDownload, onCorrect }) {
  const { t, i18n } = useTranslation('admin');
  const [copiedKey, setCopiedKey] = useState(null);
  const { copy } = useClipboard({ onSuccess: (id) => setCopiedKey(id) });
  const onCopy = (row) => copy(row.sha256, rowKey(row));

  const columns = [
    {
      key: 'document', label: t('legalDocuments.columns.document'),
      render: (r) => (documentLabelKey(r.document) ? t(documentLabelKey(r.document)) : <span style={monoStyle}>{r.document}</span>),
    },
    { key: 'version', label: t('legalDocuments.columns.version'), width: '90px', render: (r) => r.version },
    {
      key: 'effective_from', label: t('legalDocuments.columns.effectiveFrom'), width: '140px',
      render: (r) => formatDateTime(r.effective_from, i18n.language),
    },
    {
      key: 'created_at', label: t('legalDocuments.columns.createdAt'), width: '140px',
      render: (r) => formatDateTime(r.created_at, i18n.language),
    },
    {
      key: 'status', label: t('legalDocuments.columns.status'), width: '120px',
      render: (r) => {
        const s = statusOf(r, list);
        return <span style={chipStyle(STATUS_PALETTE[s])}>{t(`legalDocuments.status.${s}`)}</span>;
      },
    },
    { key: 'links', label: t('legalDocuments.columns.links'), width: '120px', render: (r) => <VersionLinks row={r} /> },
    {
      key: 'copy', label: t('legalDocuments.columns.copy'), width: '200px',
      render: (r) => <HashCell row={r} onDownload={onDownload} copiedKey={copiedKey} onCopy={onCopy} />,
    },
    {
      key: 'actions', label: t('legalDocuments.columns.actions'), width: '90px',
      render: (r) => (canCorrect(r) ? (
        <button type="button" style={linkButtonStyle} onClick={() => onCorrect(r)}>{t('legalDocuments.correct')}</button>
      ) : null),
    },
  ];

  return (
    <div style={{ background: COLORS.surface, borderRadius: RADII.control, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
      <AdminTable
        columns={columns}
        rows={list ? list.versions : []}
        loading={loading}
        emptyMessage={t('legalDocuments.empty')}
        getRowKey={rowKey}
      />
    </div>
  );
}
